import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useEffect, useRef, useState } from 'react';
import type { DragEvent, FC, FormEvent } from 'react';
import { FilePreview } from './FilePreview';
import './FileUploader.css';

export interface Props {
  onChange: (value: File[]) => void;
  hasError: boolean;
  multiple?: boolean;
  loading?: boolean;
  accept?: string;
  maxSizeInMB?: number;
  confirmRepeatedUploadHeaderMsg?: string;
  onClear?: (callback: () => void) => void;
}

export const FileUploader: FC<Props> = (props: Props) => {
  const {
    generic: {
      components: { fileUploader: fileUploaderMsg },
    },
  } = useLocale();

  const [fileList, setFileList] = useState<File[]>([]);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const confirmRepeatedUploadDialog = useSimpleConfirmDialog({
    btnConfirmAccent: 'positive',
  });
  const confirmRepeatedUploadVendors = (): Promise<boolean> =>
    confirmRepeatedUploadDialog.show({
      header:
        props.confirmRepeatedUploadHeaderMsg ||
        fileUploaderMsg.confirmDialog.header,
      subHeader: fileUploaderMsg.confirmDialog.subHeader.replace(
        '{fileName}',
        fileList.map((file) => file.name).join(', '),
      ),
      btnSecondaryConfirm: fileUploaderMsg.confirmDialog.btnSecondaryConfirm,
      btnConfirm: fileUploaderMsg.confirmDialog.btnConfirm,
    });

  const refInput = useRef<HTMLInputElement>(null);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    setIsActive(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    setIsActive(false);
  };

  const addFileToFileListWithConfirmOverwriting = async (
    handledFileList: File[],
  ) => {
    const isFileAlreadyUploaded = !props.multiple && fileList.length > 0;
    if (!isFileAlreadyUploaded || (await confirmRepeatedUploadVendors())) {
      addFileToFilelist(handledFileList);
    }
  };

  const filesAreFitBySize = (fileListToCheck: File[]): boolean => {
    const maxSizeInBytes = props.maxSizeInMB
      ? props.maxSizeInMB * 1024 * 1024
      : Number.POSITIVE_INFINITY;

    return fileListToCheck.every((file) => file.size <= maxSizeInBytes);
  };

  const addFileToFilelist = (fileListToAdd: File[]) => {
    const newFileList = props.multiple
      ? [...fileList, ...fileListToAdd].reduce((acc: File[], curr: File) => {
          if (!acc.find((file: File) => file.name === curr.name)) {
            acc.push(curr);
          }
          return acc;
        }, [])
      : fileListToAdd;

    if (!fileListHasAllowedExt(newFileList)) {
      const allowedExtensions = props.accept!.split(',');
      const allowedExtensionsString =
        allowedExtensions.length > 1
          ? allowedExtensions
              .slice(0, -1)
              .join(', ')
              .concat(
                ` ${fileUploaderMsg.or} ${allowedExtensions[allowedExtensions.length - 1]}`,
              )
          : allowedExtensions[0];

      setError(`${fileUploaderMsg.extError}${allowedExtensionsString}`);
    } else if (!filesAreFitBySize(newFileList)) {
      setError(`${fileUploaderMsg.sizeError}${props.maxSizeInMB} Mb`);
    } else {
      setError(null);
      setFileList(newFileList);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    setIsActive(false);

    const handledFileList = props.multiple
      ? Array.from(e.dataTransfer.files)
      : // Fixed noUncheckedIndexedAccess NIM-19408
        [e.dataTransfer.files[0]!];

    addFileToFileListWithConfirmOverwriting(handledFileList);
  };

  const handleChange = (e: FormEvent<HTMLInputElement>) => {
    setIsActive(false);

    if (!e.currentTarget.files || !e.currentTarget.files.length) {
      return;
    }

    const handledFileList = props.multiple
      ? Array.from(e.currentTarget.files)
      : // Fixed noUncheckedIndexedAccess NIM-19408
        [e.currentTarget.files[0]!];

    addFileToFileListWithConfirmOverwriting(handledFileList);
  };

  const selectFile = () => {
    refInput.current?.click();
  };

  const fileListHasAllowedExt = (fileListToCheck: File[]) => {
    if (!props.accept) {
      return true;
    }

    const allowedExtensions = props.accept.split(',');
    const areAllFilesAllowed = fileListToCheck.every((file) => {
      const fileExt = file.name.slice(file.name.lastIndexOf('.'));
      return allowedExtensions.includes(fileExt);
    });

    return areAllFilesAllowed;
  };

  useEffect(() => {
    if (props.onClear) {
      props.onClear(() => {
        setFileList([]);
        if (refInput.current) {
          refInput.current.value = '';
        }
      });
    }
  }, [props.onClear]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.onChange): callback change should not fire it
  useEffect(() => {
    props.onChange(fileList);
  }, [fileList]);

  return (
    <div className='flex flex-col gap-1'>
      <div
        className={`file-uploader ${isActive ? 'active' : ''} ${
          props.hasError || error ? 'invalid' : ''
        }`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Icon
          glyph='fa-arrow-up-from-bracket'
          iconStyle='regular'
          size='lg'
          className={classNames(
            isActive ? 'invisible' : 'visible',
            'text-gray-800',
          )}
        />
        <span className='text-base'>{fileUploaderMsg.DnDyourFileHere}</span>
        <div
          className={classNames(
            isActive ? 'invisible' : 'visible',
            'flex flex-col items-center gap-2',
          )}
        >
          <span className='text-xs text-gray-900 opacity-50'>
            {fileUploaderMsg.or}
          </span>
          <label htmlFor='file-input'>
            <Button
              type='button'
              variant='secondary'
              accent='positive'
              onClick={selectFile}
            >
              <span className='font-bold'>{fileUploaderMsg.browseFile}</span>
            </Button>
          </label>
        </div>
        <input
          type='file'
          id='file-input'
          accept={props.accept}
          onChange={handleChange}
          style={{ display: 'none' }}
          ref={refInput}
          multiple={props.multiple}
        />
      </div>
      {fileList.map((file, index) => (
        <FilePreview
          key={`${file.name}-${file.lastModified}`}
          file={file}
          loading={props.loading}
          onRemoveFile={() => {
            const newFileList = fileList.filter((_, i) => i !== index);
            setFileList(newFileList);

            if (refInput.current) {
              refInput.current.value = '';
            }
          }}
        />
      ))}
      {error && <div className='text-sm text-red-800'>{error}</div>}
      {confirmRepeatedUploadDialog.render()}
    </div>
  );
};
